/**
 * preview-source.ts — logika wyboru ŹRÓDŁA załączników pokazywanych przez element
 * layoutu `document_preview_view` (panel podglądu dokumentu, `DocumentPreviewPanel`).
 *
 * Cztery tryby (zapisywane w `element.settings` → `layout_config`):
 *  - `all`     — wszystkie załączniki rekordu (kolejność jak z `GET /api/attachments`,
 *                czyli `isPrimary` desc, `createdAt` desc — patrz backend route).
 *  - `primary` — wyłącznie załącznik oznaczony `isPrimary` (0 lub 1 element).
 *  - `latest`  — najnowszy załącznik PO `createdAt` (niezależnie od `isPrimary` —
 *                w odróżnieniu od domyślnej kolejności API, która primary stawia
 *                zawsze pierwszy nawet gdy nie jest najnowszy).
 *  - `field`   — wartość pola dodatkowego rekordu (`system_module_fields`, typ
 *                `file`/`image`/`gallery`). Wartość pola file/image = string
 *                (`record_attachments.id`), gallery = string[] (bierzemy PIERWSZY
 *                element — patrz nagłówek `use-preview-source-fields.ts` po
 *                uzasadnienie tego wyboru formatu wartości i czemu NIE jest to
 *                surowy klucz MinIO).
 *
 * Pure — zero zależności od Reacta/query, łatwe do testowania jednostkowego.
 */

import type { RecordAttachmentDTO } from '@saas/shared';

export type DocumentPreviewSourceMode = 'all' | 'primary' | 'latest' | 'field';

const SOURCE_MODES: ReadonlySet<string> = new Set(['all', 'primary', 'latest', 'field']);

export interface DocumentPreviewSourceConfig {
  source: DocumentPreviewSourceMode;
  /** Slug pola dodatkowego — tylko istotny gdy `source === 'field'`. */
  fieldSlug: string | null;
}

/** Odczytuje konfigurację źródła z `element.settings` (`layout_config`), z defaultami. */
export function readPreviewSourceSettings(
  settings: Record<string, unknown> | undefined,
): DocumentPreviewSourceConfig {
  const rawSource = settings?.source;
  const source: DocumentPreviewSourceMode =
    typeof rawSource === 'string' && SOURCE_MODES.has(rawSource)
      ? (rawSource as DocumentPreviewSourceMode)
      : 'all';
  const rawFieldSlug = settings?.fieldSlug;
  const fieldSlug = typeof rawFieldSlug === 'string' && rawFieldSlug ? rawFieldSlug : null;
  return { source, fieldSlug };
}

/**
 * Wyciąga `record_attachments.id` z surowej wartości pola dodatkowego typu
 * file/image (string) lub gallery (string[] — bierzemy pierwszy element).
 * Każda inna forma (null/undefined/liczba/obiekt) → `null` (brak dopasowania,
 * degradacja do pustej listy w `resolvePreviewAttachments`).
 */
function extractAttachmentIdFromFieldValue(value: unknown): string | null {
  if (typeof value === 'string' && value) return value;
  if (Array.isArray(value)) {
    const first = value[0];
    return typeof first === 'string' && first ? first : null;
  }
  return null;
}

/**
 * Rozwiązuje listę załączników do pokazania w `DocumentPreviewPanel` dla danej
 * konfiguracji źródła. `attachments` to PEŁNA lista rekordu (`GET /api/attachments`,
 * już pobrana — ta funkcja tylko filtruje/wybiera, nie robi żadnego fetcha).
 *
 * `extValues` — bieżące wartości pól dodatkowych rekordu (slug → wartość), istotne
 * TYLKO dla `source === 'field'`. Brak (create-mode bez zapisanych wartości, albo
 * source inny niż `field`) → traktowane jak `undefined` na danym slugu.
 */
export function resolvePreviewAttachments(
  config: DocumentPreviewSourceConfig,
  attachments: RecordAttachmentDTO[],
  extValues?: Record<string, unknown>,
): RecordAttachmentDTO[] {
  switch (config.source) {
    case 'primary': {
      const primary = attachments.find((a) => a.isPrimary);
      return primary ? [primary] : [];
    }
    case 'latest': {
      if (attachments.length === 0) return [];
      return [
        attachments.reduce((latest, current) =>
          new Date(current.createdAt).getTime() > new Date(latest.createdAt).getTime()
            ? current
            : latest,
        ),
      ];
    }
    case 'field': {
      // TODO-DECISION: efektywnie no-op do rozszerzenia systemowych typów pól o
      // file/image/gallery w backendzie (dziś whitelist ext-fields ich nie ma — żadne
      // pole nie może wypełnić `extValues`, więc ta gałąź zawsze degraduje do []).
      // Patrz docs/OPEN_QUESTIONS.md § Załączniki kartotek + „Typ pola file / załączniki
      // w ext-fields (F12)".
      if (!config.fieldSlug) return [];
      const attachmentId = extractAttachmentIdFromFieldValue(extValues?.[config.fieldSlug]);
      if (!attachmentId) return [];
      const match = attachments.find((a) => a.id === attachmentId);
      return match ? [match] : [];
    }
    case 'all':
    default:
      return attachments;
  }
}
